import { useState } from "react";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import { Box, Profile } from "iconsax-react";
import { Table, Button, Dropdown, DropdownItem } from "@/shared/ui";
import { PERMISSIONS } from "@/shared/consts";
import { useAuth } from "@/shared/hooks";
import { MoreIcon } from "@/shared/assets/icons";
import { formatMoneyKzt } from "@/features/accounting/shared";
import type { WarehouseGroup } from "../hooks";

interface Props {
  groups: WarehouseGroup[];
  onGroupSelect: (groupName: string) => void;
  onBulkTransfer?: (fromEmployeeId: number, fromEmployeeName: string) => void;
  categoryLabel?: string;
}

export default function WarehouseGroupsTable({ groups, onGroupSelect, onBulkTransfer, categoryLabel }: Props) {
  const { t } = useTranslation("WarehousePage");
  const { hasPermission } = useAuth();
  const canAssignAssets = hasPermission(PERMISSIONS.WAREHOUSE.ASSIGN_ASSETS);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);

  return (
    <Table.Table>
        <Table.Header>
          <tr>
            <Table.HeadCell>{t("table.groupName")}</Table.HeadCell>
            <Table.HeadCell>{t("table.quantity")}</Table.HeadCell>
            <Table.HeadCell>{t("table.totalAmount")}</Table.HeadCell>
            {canAssignAssets && onBulkTransfer && <Table.HeadCell>{t("table.actions")}</Table.HeadCell>}
          </tr>
        </Table.Header>
        <Table.Body>
          {groups.map((group, index) => {
            const isEven = index % 2 === 0;
            const avatarBg = isEven ? "bg-grey-50 dark:bg-grey-900" : "bg-white dark:bg-grey-950";
            const isUnassigned = group.groupName === t("groups.unassigned");
            const firstUnit = group.units[0];
            const employeeId = firstUnit?.assigned_to;

            return (
              <Table.Row
                key={group.groupName}
                onClick={() => onGroupSelect(group.groupName)}
                className="cursor-pointer hover:surface-component-hover">
                <Table.Cell isBold>
                  <div className="flex items-center gap-2">
                    <div
                      className={cn(
                        "w-7 aspect-square radius-xs content-action-neutral flex items-center justify-center",
                        avatarBg,
                      )}>
                      {isUnassigned ? (
                        <Box size={16} color="currentColor" />
                      ) : (
                        <Profile size={16} color="currentColor" />
                      )}
                    </div>
                    <span>{group.groupName}</span>
                  </div>
                </Table.Cell>
                <Table.Cell>
                  <span className="text-label-sm content-base-primary">{group.count}</span>
                </Table.Cell>
                <Table.Cell>
                  <span className="text-label-sm content-base-primary">{formatMoneyKzt(group.totalAmount)}</span>
                </Table.Cell>
                {canAssignAssets && onBulkTransfer && (
                  <Table.Cell>
                    {!isUnassigned && employeeId && (
                      <Dropdown
                        open={openDropdownId === group.groupName}
                        onClose={() => setOpenDropdownId(null)}
                        align="right"
                        direction={index === groups.length - 1 ? "top" : "bottom"}>
                        <Button
                          variant="secondary"
                          isIconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenDropdownId(openDropdownId === group.groupName ? null : group.groupName);
                          }}
                          className="w-8! radius-xs! p-0!">
                          <MoreIcon />
                        </Button>
                        <DropdownItem
                          onClick={() => {
                            onBulkTransfer(employeeId, group.groupName);
                            setOpenDropdownId(null);
                          }}>
                          {t("actions.bulkTransfer", { cat_name: categoryLabel })}
                        </DropdownItem>
                      </Dropdown>
                    )}
                  </Table.Cell>
                )}
              </Table.Row>
            );
          })}
        </Table.Body>
    </Table.Table>
  );
}
